import React, { use, useMemo, type ReactNode } from 'react';
import {
	type WorkflowStageTaskViewModel,
	type FullVisitBucketViewModel,
	type FullVisitViewModel,
	type FullVisitBucketItemViewModel,
	type InterventionTaskViewModel,
	SnomedConceptTypeEnum,
	type OasisTaskViewModel
} from '@generated';
import { flattenInterventionTasksFromVisit, flattenOasisTasksFromVisit, flattenWorkflowStageTasksFromVisit } from './util';
import { getVisitRoutePath } from '../../pages/visits/utils';
import { useNavigate } from 'react-router-dom';
import { VisitEvaluationProvider } from '../frequencies/evaluations/VisitEvaluationContext';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useGetFullVisitOptions } from './queries';

interface VisitProviderResult extends FullVisitViewModel {
	selectedVisitBucket: FullVisitBucketViewModel;
	selectedVisitBucketItem?: FullVisitBucketItemViewModel;
	workflowStageTasks: WorkflowStageTaskViewModel[];
	interventionTasks: InterventionTaskViewModel[];
	oasisTasks: OasisTaskViewModel[];
	visitFindings: string[];
	navigateToPreviousItem?: () => void;
	navigateToNextItem?: () => void;
	refetchVisit: () => void;
}

const VisitContext = React.createContext<VisitProviderResult | null>(null);

export function VisitProvider({
	stayId,
	visitId,
	selectedBucketItemId,
	selectedVisitBucket,
	children
}: {
	stayId: string;
	visitId: string;
	selectedBucketItemId: string;
	selectedVisitBucket: FullVisitBucketViewModel;
	children: (selectedVisitBucketItem?: FullVisitBucketItemViewModel) => ReactNode;
}): ReactNode {
	const navigate = useNavigate();
	const { data: visit, refetch } = useSuspenseQuery(useGetFullVisitOptions(visitId));
	const visitTasks = useMemo(() => {
		const interventionTasks = flattenInterventionTasksFromVisit(visit);
		return {
			interventionTasks,
			workflowStageTasks: flattenWorkflowStageTasksFromVisit(visit),
			oasisTasks: flattenOasisTasksFromVisit(visit),
			visitFindings: interventionTasks
				.flatMap(x => x.visitConcepts)
				.filter(x => x.snomedConceptTypeId === SnomedConceptTypeEnum.Finding)
				.flatMap(x => x.conceptId)
		};
	}, [visit]);

	const selectedVisitBucketItem = useMemo(
		() => selectedVisitBucket.orderedItems?.find(x => x.id === selectedBucketItemId),
		[selectedVisitBucket.orderedItems, selectedBucketItemId]
	);

	const navigateToPreviousItem = selectedVisitBucketItem?.previousBucketItemId
		? () => navigate(getVisitRoutePath(stayId, visitId, selectedVisitBucket.id, selectedVisitBucketItem.previousBucketItemId))
		: undefined;

	console.log(selectedVisitBucketItem);
	const navigateToNextItem = selectedVisitBucketItem?.nextBucketItemId
		? () => navigate(getVisitRoutePath(stayId, visitId, selectedVisitBucket.id, selectedVisitBucketItem.nextBucketItemId))
		: undefined;

	const contextValue = {
		...visit,
		selectedVisitBucket,
		...visitTasks,
		selectedVisitBucketItem,
		navigateToPreviousItem,
		navigateToNextItem,
		refetchVisit: refetch
	};

	return visit.containsEvaluation ? (
		<VisitContext value={contextValue}>
			<VisitEvaluationProvider visitId={visit.id}>{children(selectedVisitBucketItem)}</VisitEvaluationProvider>
		</VisitContext>
	) : (
		<VisitContext value={contextValue}>{children(selectedVisitBucketItem)}</VisitContext>
	);
}

export function useVisit(): VisitProviderResult {
	const context = use(VisitContext);

	if (context === null) {
		throw new Error('useVisitContext must be used within a VisitProvider');
	}

	return context;
}
